import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { DateFormatter } from 'infrastructure/functions';
import { API_READINGS_ACTIONS_DELETE } from 'services/dataService/resources';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useTranslation } from 'react-i18next';
import { UserType } from 'infrastructure/enums';
import {
  getCcmInteractionTimeKey,
  getRpmInteractionTimeKey,
} from 'components/organisms/interactiveTime/hooks';

import type {
  IDeleteCommentData,
  IRpmPatientActivity,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { QueryKey, UseMutateAsyncFunction } from '@tanstack/react-query';

const deleteActivityCommentFn = async (postData: IDeleteCommentData) => {
  const { guid, userType } = postData;
  if (userType !== UserType.Admin) {
    throw new Error('You do not have permission to delete comments');
  }

  const url = API_READINGS_ACTIONS_DELETE;
  const { error, data } = await dataService.createOne<void>(url, { guid });

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export type TDeleteCommentFn = UseMutateAsyncFunction<
  void | undefined,
  unknown,
  IDeleteCommentData,
  unknown
>;

interface Props {
  getActivityQueryKey: QueryKey;
  patientGuid: string;
  tableRowIdx: number | string;
}

export const useDeleteActivityComment = ({
  getActivityQueryKey,
  tableRowIdx,
  patientGuid: propsPatientGuid,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation({
    mutationKey: ['deleteCommentAction', tableRowIdx, propsPatientGuid],
    mutationFn: deleteActivityCommentFn,
    async onSuccess(responseData, variables) {
      notificationController.success({
        message: t('labels.commentDeleted'),
      });
      const today = new Date();
      const formattedDate = DateFormatter({ date: today });
      const rpmInteractiveTime = getRpmInteractionTimeKey(
        propsPatientGuid,
        formattedDate,
      );
      queryClient.invalidateQueries(rpmInteractiveTime);
      const ccmInteractiveTime = getCcmInteractionTimeKey(
        propsPatientGuid,
        formattedDate,
      );
      queryClient.invalidateQueries(ccmInteractiveTime);
      queryClient.setQueryData<IRpmPatientActivity>(
        getActivityQueryKey,
        (oldData) => {
          if (oldData) {
            const newData = structuredClone(oldData);
            newData.data.forEach((item) => {
              item.comments = item.comments.filter(
                (comment) => comment.guid !== variables.guid,
              );
            });

            return newData;
          }

          return oldData;
        },
      );
    },
    onError() {
      notificationController.error({
        message: t('labels.commentDeleteError'),
      });
    },
  });

  return {
    deleteActivityComment: mutateAsync,
    deleteCommentLoading: isLoading,
  };
};
